import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useApiMutation } from '@/hooks/useApiMutation';
import { showSuccess, showError } from '@/lib/toastHelpers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pencil, Trash2, Plus, FlaskConical, Search } from 'lucide-react';

interface DensityRow {
  _id: string;
  ingredient: string;
  canonical: string;
  aliases: string[];
  tsp: number | null;
  tbsp: number | null;
  cup: number | null;
  fl_oz: number | null;
  piece: number | null;
}

const EMPTY: Omit<DensityRow, '_id'> = { ingredient: '', canonical: '', aliases: [], tsp: null, tbsp: null, cup: null, fl_oz: null, piece: null };

const UNIT_LABELS: Record<'tsp' | 'tbsp' | 'cup' | 'fl_oz' | 'piece', string> = {
  tsp: 'tsp (g)', tbsp: 'tbsp (g)', cup: 'cup (g)', fl_oz: 'fl oz (g)', piece: 'Per Piece (g)',
};

function DensityForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: Omit<DensityRow, '_id'>;
  onSave: (data: Omit<DensityRow, '_id'>) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState(initial);
  const set = (field: string, val: string) => {
    const num = val === '' ? null : parseFloat(val);
    setForm(prev => ({ ...prev, [field]: typeof num === 'number' && isNaN(num) ? prev[field as keyof typeof prev] : (val === '' ? null : num ?? val) }));
  };

  const handleSave = () => {
    // Canonical is the internal lookup key — not admin-editable, always derived from
    // the display name so it stays in sync and admins don't need to understand it.
    const aliases = [...new Set(aliasesText.split(',').map(a => a.trim().toLowerCase()).filter(Boolean))];
    onSave({ ...form, canonical: form.ingredient.toLowerCase().trim(), aliases });
  };

  // Aliases are edited as a comma-separated string for convenience; stored as an array.
  const [aliasesText, setAliasesText] = useState((initial.aliases || []).join(', '));

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label>Display name</Label>
        <Input value={form.ingredient} onChange={e => setForm(p => ({ ...p, ingredient: e.target.value }))} placeholder="e.g. brown sugar" />
      </div>
      <div className="space-y-1">
        <Label>Aliases (comma-separated)</Label>
        <Input
          value={aliasesText}
          onChange={e => setAliasesText(e.target.value)}
          placeholder="e.g. pepper, peppercorn, ground pepper"
        />
        <p className="text-xs text-muted-foreground">
          Other names that should match this entry — "peppercorn" as an alias lets "Whole Peppercorn"
          or "Szechuan whole peppercorn" find this row even though neither is the display name.
        </p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {(['tsp', 'tbsp', 'cup', 'fl_oz', 'piece'] as const).map(unit => (
          <div key={unit} className="space-y-1">
            <Label>{UNIT_LABELS[unit]}</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={form[unit] ?? ''}
              onChange={e => set(unit, e.target.value)}
              placeholder="—"
            />
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving || !form.ingredient.trim()}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default function DensityReferences() {
  const [editingRow, setEditingRow] = useState<DensityRow | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery<{ rows: DensityRow[] }>({
    queryKey: ['/api/admin/density-references'],
    queryFn: () => apiRequest('GET', '/api/admin/density-references') as unknown as Promise<{ rows: DensityRow[] }>,
  });

  const rows = data?.rows ?? [];
  const q = search.trim().toLowerCase();
  const filteredRows = q
    ? rows.filter(r => r.ingredient.toLowerCase().includes(q) || r.canonical.toLowerCase().includes(q))
    : rows;

  const createMutation = useApiMutation({
    endpoint: () => '/api/admin/density-references',
    method: 'POST',
    invalidate: [['/api/admin/density-references']],
    successMessage: 'Row added',
    errorMessage: 'Failed to add row',
  });

  const updateMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/density-references/${vars.id}`,
    method: 'PATCH',
    invalidate: [['/api/admin/density-references']],
    successMessage: 'Row updated',
    errorMessage: 'Failed to update row',
  });

  const deleteMutation = useApiMutation({
    endpoint: (vars: { id: string }) => `/api/admin/density-references/${vars.id}`,
    method: 'DELETE',
    invalidate: [['/api/admin/density-references']],
    successMessage: 'Row deleted',
    errorMessage: 'Failed to delete row',
  });

  const handleAdd = (form: Omit<DensityRow, '_id'>) => {
    createMutation.mutate(form as any, { onSuccess: () => setAddOpen(false) });
  };

  const handleUpdate = (form: Omit<DensityRow, '_id'>) => {
    if (!editingRow) return;
    updateMutation.mutate({ id: editingRow._id, ...form } as any, { onSuccess: () => setEditingRow(null) });
  };

  const handleDelete = (id: string) => {
    if (!confirm('Delete this entry?')) return;
    deleteMutation.mutate({ id } as any);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold tracking-tight">Density References</h1>
          <p className="text-muted-foreground">Grams-per-unit table used for volume → gram conversion during AI recipe import</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-2">
          <Plus className="h-4 w-4" /> Add Entry
        </Button>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
          <div>
            <div className="flex items-center gap-2">
              <FlaskConical className="w-5 h-5" />
              <CardTitle>Density Table</CardTitle>
            </div>
            <CardDescription>
              {q ? `${filteredRows.length} of ${rows.length} entries` : `${rows.length} entries`} · sorted alphabetically by canonical name
            </CardDescription>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search ingredients…"
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <p className="p-6 text-sm text-muted-foreground">Loading…</p>}
          {!isLoading && rows.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground">No entries yet. Run the seed script or add entries manually.</p>
          )}
          {!isLoading && rows.length > 0 && filteredRows.length === 0 && (
            <p className="p-6 text-sm text-muted-foreground">No entries match "{search}".</p>
          )}
          {filteredRows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide bg-muted/30">
                    <th className="text-left px-4 py-2">Display name</th>
                    <th className="text-left px-4 py-2">Canonical</th>
                    <th className="text-left px-4 py-2">Aliases</th>
                    <th className="text-right px-4 py-2">tsp (g)</th>
                    <th className="text-right px-4 py-2">tbsp (g)</th>
                    <th className="text-right px-4 py-2">cup (g)</th>
                    <th className="text-right px-4 py-2">fl oz (g)</th>
                    <th className="text-right px-4 py-2">Per Piece (g)</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredRows.map(row => (
                    <tr key={row._id} className="hover:bg-muted/20">
                      <td className="px-4 py-2 capitalize">{row.ingredient}</td>
                      <td className="px-4 py-2 text-muted-foreground">{row.canonical}</td>
                      <td className="px-4 py-2 text-muted-foreground text-xs">{row.aliases?.length ? row.aliases.join(', ') : '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.tsp ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.tbsp ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.cup ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.fl_oz ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{row.piece ?? '—'}</td>
                      <td className="px-4 py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingRow(row)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDelete(row._id)}
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Add Density Entry</DialogTitle></DialogHeader>
          <DensityForm initial={EMPTY} onSave={handleAdd} onCancel={() => setAddOpen(false)} saving={createMutation.isPending} />
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editingRow} onOpenChange={open => !open && setEditingRow(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Edit Density Entry</DialogTitle></DialogHeader>
          {editingRow && (
            <DensityForm
              initial={{ ingredient: editingRow.ingredient, canonical: editingRow.canonical, aliases: editingRow.aliases || [], tsp: editingRow.tsp, tbsp: editingRow.tbsp, cup: editingRow.cup, fl_oz: editingRow.fl_oz, piece: editingRow.piece }}
              onSave={handleUpdate}
              onCancel={() => setEditingRow(null)}
              saving={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
